import type { GameStatus } from './types';

/*
 * When to re-read a game. No Vue, no timers — the policy is data so the numbers can be argued
 * about in one place and tested without waiting for them.
 *
 * docs/decisions/0009-polling-not-websockets.md is the reasoning, including the cost arithmetic
 * that makes polling the cheap option rather than the lazy one.
 */

export const POLL_INTERVAL_MS = {
  /** A grid people are actively picking on. Fast enough to feel live while somebody watches. */
  OPEN: 10_000,
  /** Waiting for the host to type two numbers. A third of the rate is plenty. */
  LOCKED: 30_000,
} as const;

/** A tab left open through an outage has to recover on its own, so backoff has a ceiling. */
export const MAX_POLL_INTERVAL_MS = 120_000;

/**
 * How long a tab may keep reading without anybody touching it.
 *
 * A grid left open on a second monitor after the match would otherwise poll all afternoon for a
 * reader who is not there. At the limit it stops and says so, and only the Refresh button starts
 * it again — deliberately not a stray tap, so the board never shifts under somebody's fingers.
 */
export const IDLE_LIMIT_MS = 60 * 60 * 1000;

/** Whether a tab has gone quiet long enough to stop reading. */
export const isIdle = (msSinceActivity: number): boolean => msSinceActivity >= IDLE_LIMIT_MS;

/**
 * How often to re-read a game in this state, or null to stop.
 *
 * Null for a finished or cancelled game because nothing about one can change — and null for no
 * game at all, so a load that failed is retried by the button rather than by a timer.
 */
export function pollIntervalFor(status: GameStatus | null | undefined): number | null {
  switch (status) {
    case 'OPEN':
      return POLL_INTERVAL_MS.OPEN;
    case 'LOCKED':
      return POLL_INTERVAL_MS.LOCKED;
    default:
      return null;
  }
}

/** Exponential backoff on consecutive failures, capped so polling never effectively stops. */
export function backoffFor(base: number, consecutiveFailures: number): number {
  const doublings = Math.max(0, consecutiveFailures);
  return Math.min(base * 2 ** doublings, MAX_POLL_INTERVAL_MS);
}
